import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Divider,
} from '@mui/material';
import {
  Insights as OverviewIcon,
  AccountTree as AuthorityIcon,
  Timeline as LifecycleIcon,
  VerifiedUser as EnterpriseIcon,
  Shield as ShieldIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { sidebar } from '../theme';
import IngestionFlowDiagram from '../components/IngestionFlowDiagram';

// Positioning pillar row (not a card — enterprise information hierarchy over decoration)
interface PillarProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Pillar: React.FC<PillarProps> = ({ icon, title, description }) => (
  <Stack direction="row" spacing={2} sx={{ py: 1.75 }}>
    <Box sx={{ color: 'primary.main', mt: 0.5 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Stack>
);

const Stat: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <Box>
    <Typography variant="h4" fontWeight={700}>{value}</Typography>
    <Typography variant="caption" sx={{ opacity: 0.7 }}>{label}</Typography>
  </Box>
);

const Landing: React.FC = () => {
  const navigate = useNavigate();

  const pillars = [
    {
      icon: <OverviewIcon />,
      title: 'Executive Confidence',
      description: 'A single portfolio view of compliance position, upcoming obligations and risks requiring attention — built for board and regulator scrutiny.',
    },
    {
      icon: <AuthorityIcon />,
      title: 'Authority Intelligence',
      description: 'Every authority tracked as a connected data source, with ingestion status, update cadence and provenance made explicit.',
    },
    {
      icon: <LifecycleIcon />,
      title: 'Consent Lifecycle',
      description: 'Each consent treated as a regulatory asset: origin authority, legislation, project relationship, timeline, conditions and audit history.',
    },
    {
      icon: <EnterpriseIcon />,
      title: 'Built for Complex Programmes',
      description: 'Built for infrastructure organisations operating across multiple authorities, jurisdictions and projects at once.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top nav */}
      <Box sx={{ bgcolor: sidebar.bg, borderBottom: `1px solid ${sidebar.border}` }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShieldIcon sx={{ color: sidebar.accent }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>ASSERTION</Typography>
            </Stack>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              endIcon={<ArrowIcon />}
              sx={{ bgcolor: 'primary.main' }}
            >
              Enter Platform
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: sidebar.bg, color: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography sx={{ color: sidebar.accent, fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.75rem', mb: 2 }}>
                REGULATORY INTELLIGENCE PLATFORM
              </Typography>
              <Typography
                variant="h2"
                fontWeight={700}
                gutterBottom
                sx={{ fontSize: { xs: '2.25rem', md: '3rem' }, lineHeight: 1.2 }}
              >
                A trusted, continuously maintained view of regulatory consents
              </Typography>
              <Typography sx={{ opacity: 0.75, mb: 4, maxWidth: 560, fontSize: '1.05rem' }}>
                Assertion gives infrastructure organisations a single source of truth for consents
                across authorities, projects and jurisdictions — from origination through to discharge.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/dashboard')}
                  sx={{ bgcolor: 'primary.main', px: 4, py: 1.25 }}
                >
                  View Portfolio Overview
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/authorities')}
                  sx={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff', px: 4, py: 1.25, '&:hover': { borderColor: '#fff' } }}
                >
                  Explore Authorities
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ bgcolor: sidebar.bgHover, border: `1px solid ${sidebar.border}`, borderRadius: 1.5, p: 3 }}>
                <Typography variant="overline" sx={{ color: sidebar.textMuted }}>Live Portfolio Snapshot</Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Consent categories tracked</Typography>
                    <Typography variant="body2" fontWeight={700}>9</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Consent types mapped</Typography>
                    <Typography variant="body2" fontWeight={700}>41</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Authorities tracked</Typography>
                    <Typography variant="body2" fontWeight={700}>21</Typography>
                  </Stack>
                  <Divider sx={{ borderColor: sidebar.border }} />
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    Source-mapped with provenance and update cadence for every authority.
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats strip */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Stack direction="row" spacing={5} divider={<Divider orientation="vertical" flexItem />} flexWrap="wrap">
            <Stat value="9" label="Consent Categories" />
            <Stat value="41" label="Consent Types" />
            <Stat value="7" label="Lifecycle Phases" />
            <Stat value="21" label="Authorities tracked" />
          </Stack>
        </Container>
      </Box>

      {/* Product education */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>How Assertion works</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mb: 4 }}>
          Assertion turns changing authority information into a trusted consent register and a clear set of operational actions.
        </Typography>
        <IngestionFlowDiagram />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Records remain supported by retained source evidence throughout the journey; uncertain records pause at Review rather than being published automatically.
        </Typography>
      </Container>

      {/* Pillars */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
        <Box mb={4}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Built for regulatory complexity
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
            Assertion is not a generic tracker — it is structured around how infrastructure
            consents actually move through authorities, legislation and delivery programmes.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {pillars.map((pillar, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Pillar {...pillar} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', py: 6 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            See the portfolio, not just the paperwork
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Open the platform to review compliance position, connected authorities and consent lifecycles.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/dashboard')} sx={{ px: 5, py: 1.25 }}>
            Enter Platform
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: sidebar.bg, color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={600}>
                Assertion
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Regulatory Intelligence Platform
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ opacity: 0.7, textAlign: { md: 'right' } }}>
                © {new Date().getFullYear()} Assertion. Built for infrastructure project teams.
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ opacity: 0.5 }}>
              A{' '}
              <Box
                component="a"
                href="https://linckia.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline', opacity: 0.8 },
                }}
              >
                Linckia
              </Box>
              {' '}product
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
